import useLatestRef from "../react/react-use/useLatestRef";
import { useGroopitApiContext } from "groopit-js";
import {
  AiPopulateDataModelDTO,
  AiPopulateDataModelResultDTO,
  AsyncStatus,
} from "groopit-js/client";
import React, { useContext, useRef } from "react";
import { useEffect, useState } from "react";
import { useInterval } from "react-use";
import { AiBackgroundProcessingContext } from "./AiBackgroundProcessingContext";

interface PopulateDataModelHelperProps {
  formUrl: string;
  groupId: string;
  assignmentId: string;
  additionalInputs?: Map<string, string>;
  input?: AiPopulateDataModelDTO;
  children: React.ReactNode;
  onStatus: (status: AsyncStatus) => void;
}

export const PopulateDataModelHelper = (
  props: PopulateDataModelHelperProps,
) => {
  const api = useLatestRef(useGroopitApiContext());
  const { defaultPollingIntervalMS } = useContext(
    AiBackgroundProcessingContext,
  );
  const {
    formUrl,
    groupId,
    assignmentId,
    additionalInputs,
    input,
    children,
    onStatus,
  } = props;
  const form = useRef<HTMLFormElement>(null);
  const dataInput = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<AiPopulateDataModelResultDTO>();

  useInterval(
    () => {
      // The interval should pause when result is non-null but make
      // TypesScript happy
      if (result?.status === AsyncStatus.InProgress && result.jobId) {
        api.current.ai
          .getPopulateDataModelResult(result.jobId)
          .then(setResult)
          .catch(() => {
            onStatus(AsyncStatus.Failed);
          });
      }
    },
    result?.status === AsyncStatus.InProgress ? defaultPollingIntervalMS : null,
  );

  useEffect(() => {
    if (result && dataInput.current && form.current) {
      if (result.status === AsyncStatus.Completed) {
        dataInput.current.value = JSON.stringify(result.result);
        form.current.submit();
      } else if (result.status === AsyncStatus.Failed) {
        // Do nothing.
      } else if (result.status === AsyncStatus.InProgress && result.jobId) {
        // Let the interval handle this
      } else {
        // Do nothing.
      }
    }
  }, [api, dataInput, form, onStatus, result]);

  useEffect(() => {
    if (api && input) {
      api.current.ai
        .populateDataModel(assignmentId, input)
        .then(setResult)
        .catch(() => {
          onStatus(AsyncStatus.Failed);
        });
    }
  }, [api, assignmentId, onStatus, input]);

  useEffect(() => {
    if (result) {
      onStatus(result.status);
    }
  }, [onStatus, result]);

  return (
    <>
      {children}
      <form method="POST" action={formUrl} ref={form}>
        <input type="hidden" name="groupId" value={groupId} />
        <input type="hidden" name="assignmentId" value={assignmentId} />
        {Array.from(additionalInputs ?? []).map(([k, v]) => (
          <input key={k} type="hidden" name={k} value={v} />
        ))}
        <input type="hidden" name="data" ref={dataInput} />
        {input?.content.file && (
          <>
            <input
              hidden
              name="input.Content.File.Filename"
              readOnly
              value={input?.content.file.filename}
            />
          </>
        )}
        {input?.content.url && (
          <input
            hidden
            name="input.Content.Url"
            readOnly
            value={input?.content.url.toString()}
          />
        )}
      </form>
    </>
  );
};
